// ChatManager — Thin backend client for chatbot
// Sends messages to ChatbotModule backend, processes actions locally

import * as history from './modules/history'
import * as logger from './modules/logger'
import * as diag from './modules/diagnostic'

// ─── Backend URL ──────────────────────────────────────────────────────────

const BACKEND_URL = process.env.REACT_APP_CHATBOT_BACKEND_URL || 'http://10.189.104.81:5066'

// ─── State ─────────────────────────────────────────────────────────────────

let _mode = 'backend'       // 'backend' | 'mock'
let _lastUsageDetails = null // backend-provided usage data
let _caps = null             // capabilities object from ChatWidget
let _language = 'de'
let _userId = 'anonymous'
let _camId = 0
let _sessionId = ''
let _projectId = ''
let _siteDataPath = ''

// Dashboard actions registry (set by DashboardContent, read by ChatWidget)
let _dashboardActions = null
export function setDashboardActions(actions) { _dashboardActions = actions }
export function getDashboardActions() { return _dashboardActions }

// Archive actions registry (set by ArchiveControls, read by _processActions)
let _archiveActions = null
export function setArchiveActions(actions) { _archiveActions = actions }
export function getArchiveActions() { return _archiveActions }

// MYSITE section actions registry (set by ArchiveControls when on MYSITE, read by _processActions)
let _mysiteActions = null
export function setMysiteActions(actions) { _mysiteActions = actions }

// Dashboard activation (set by Content.js when on MYSITE)
let _activateDashboard = null
export function setActivateDashboard(fn) { _activateDashboard = fn }

// Navigation registry (set by ChatWidget, provides react-router navigate)
let _navigate = null
export function setNavigate(fn) { _navigate = fn }

// Diagnostic log access (for ChatWidget export button)
export function getDiagnosticLog() { return diag.dump() }
export function getDiagnosticCount() { return diag.count() }

// Navigate to dashboard: goes to MYSITE and activates the dashboard view
export async function navigateToDashboard() {
    const conf = window.conf || {}
    const mysitePath = conf.ROUTES?.MYSITE?.path
    if (!mysitePath || !_navigate) return false
    _navigate(mysitePath)
    const ok = await _poll(() => _activateDashboard, 100, 5000)
    if (ok) { _activateDashboard(); return true }
    return false
}

// ─── Init ──────────────────────────────────────────────────────────────────

/**
 * Initialize the chatbot system.
 *
 * @param {object} [config]
 * @param {string} [config.username] - Keycloak username
 * @param {object} [config.capabilities] - capabilities object from ChatWidget
 * @param {number} [config.camId]
 * @param {string} [config.baseUrl]
 * @param {string} [config.language]
 * @param {string} [config.projectId]
 * @param {string} [config.logPath]
 */
export async function init(config = {}) {
    _mode = 'backend'

    const conf = window.conf || {}
    _camId = config.camId ?? (conf.CAMERAS?.[0]?.id ?? 0)
    _language = config.language || 'de'
    _userId = config.username || 'anonymous'
    _caps = config.capabilities || null
    _projectId = String(config.projectId || conf.CAMERAS?.[0]?.featureDetailId || '')
    _siteDataPath = conf.DATA_PATH || ''
    _sessionId = new Date().toISOString().replace(/[:.]/g, '-') + '-' + _userId

    diag.log('init', 'ChatManager initialized', {
        backendUrl: BACKEND_URL,
        mode: _mode,
        username: _userId,
        sessionId: _sessionId,
        projectId: _projectId,
        siteDataPath: _siteDataPath,
        camId: _camId,
        language: _language,
        capabilities: _flattenCapabilities(_caps),
    })

    // Logger
    logger.init({
        clientId: 'productive_system',
        username: _userId,
        projectId: _projectId,
        logPath: config.logPath || '/srv/data/sites/usage_log',
    })

    logger.logEvent('session_start', {
        capabilities: _caps,
        mode: _mode,
        userAgent: navigator.userAgent,
    })
}

// ─── Mode ──────────────────────────────────────────────────────────────────

export function getMode() { return _mode }
export function isMock() { return _mode === 'mock' }

export function getUsageDetail() {
    return _lastUsageDetails
}

/** Fetch usage state from backend. */
export async function fetchBackendUsage() {
    diag.log('usage', 'Fetching usage from backend')
    try {
        const resp = await fetch(`${BACKEND_URL}/api/usage?username=${encodeURIComponent(_userId)}`)
        if (resp.ok) {
            _lastUsageDetails = await resp.json()
            diag.log('usage', 'Usage fetched OK', {
                expensive_daily: _lastUsageDetails?.expensive?.daily?.cost,
                gatekeeper_daily: _lastUsageDetails?.gatekeeper?.daily?.cost,
            })
            return _lastUsageDetails
        }
        diag.error('usage', `Usage fetch failed: HTTP ${resp.status}`)
    } catch (e) {
        diag.error('usage', `Usage fetch error: ${e.message}`)
    }
    return null
}

/**
 * Switch chat mode (backend or mock for development).
 */
export function setMode(mode) {
    if (['mock', 'backend'].includes(mode)) {
        const prev = _mode
        _mode = mode
        diag.log('mode', `Mode switched: ${prev} → ${mode}`)
        logger.logEvent('mode_switch', { from: prev, to: mode })
    }
}

// ─── Flatten capabilities for backend API ─────────────────────────────────

function _flattenCapabilities(caps) {
    if (!caps) return {}
    return {
        dashboard: !!caps.mysite?.dashboard,
        report: !!caps.mysite?.report,
        diary: !!caps.mysite?.diary,
        download: !!caps.mysite?.download,
        events: !!caps.mysite?.events,
        mysite: !!caps.pages?.mysite,
        access: !!caps.pages?.access,
        livestream: !!caps.features?.livestream,
    }
}

// ─── Backend mode handler ──────────────────────────────────────────────────

/**
 * Send message to the Python backend — single fetch, backend handles everything.
 */
async function _handleBackend(userMessage, dashboardState, options) {
    const language = options.language || _language
    const currentPage = options.currentPage || _getCurrentPage()
    const startTime = performance.now()

    const payload = {
        message: userMessage,
        session_id: _sessionId,
        portal_capabilities: _flattenCapabilities(_caps),
        user_permissions: { username: _userId },
        current_page: currentPage,
        language,
        dashboard_state: dashboardState || null,
        project_id: _projectId,
        cam_id: _camId,
        site_data_path: _siteDataPath,
    }

    diag.log('backend', 'Sending request', {
        message: userMessage.substring(0, 100),
        page: currentPage,
        language,
        hasDashboardState: !!dashboardState,
        capabilities: payload.portal_capabilities,
    })

    history.addMessage('user', userMessage)

    let resp, json
    try {
        resp = await fetch(`${BACKEND_URL}/api/chat`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        })
        json = await resp.json()
    } catch (e) {
        diag.error('backend', `Fetch failed: ${e.message}`)
        throw e
    }

    const fetchMs = Math.round(performance.now() - startTime)

    // Store backend-provided usage
    if (json.usage) _lastUsageDetails = json.usage

    if (!resp.ok && resp.status === 429) {
        diag.log('backend', 'Rate limited (429)', { reply: json.reply?.substring(0, 80), fetchMs })
        history.addMessage('assistant', json.reply, { model: 'backend', classification: 'limit_reached' })
        return { reply: json.reply, mock: false, debug: { degraded: true } }
    }

    if (!resp.ok) {
        diag.error('backend', `HTTP ${resp.status}`, { error: json.error, fetchMs })
        throw new Error(json.error || `Backend error ${resp.status}`)
    }

    diag.log('backend', 'Response received', {
        fetchMs,
        classification: json.debug?.classification,
        gatekeeper_model: json.debug?.gatekeeper?.model,
        gatekeeper_ms: json.debug?.gatekeeper?.duration_ms,
        expensive_model: json.debug?.expensive?.model,
        expensive_ms: json.debug?.expensive?.duration_ms,
        reply_len: json.reply?.length,
        actions: json.actions?.length || 0,
        total_backend_ms: json.debug?.total_duration_ms,
    })

    const actions = json.actions || []
    const { dashboardActions, navigationDescriptions } = await _processActions(actions, language)

    history.addMessage('assistant', json.reply, {
        model: json.debug?.expensive?.model || json.debug?.gatekeeper?.model || 'backend',
        classification: json.debug?.classification,
    })

    for (const desc of navigationDescriptions) {
        history.addAction(desc)
    }

    const totalDurationMs = Math.round(performance.now() - startTime)
    logger.logChatMessage({
        userMessage,
        currentPage,
        dashboardState: dashboardState || null,
        classification: json.debug?.classification,
        gatekeeper: json.debug?.gatekeeper || undefined,
        expensive: json.debug?.expensive || undefined,
        reply: json.reply,
        actions: dashboardActions,
        totalDurationMs,
        capabilities: _caps,
        language,
        mode: _mode,
    })

    const result = { reply: json.reply, mock: false, debug: json.debug }
    if (dashboardActions.length) result.actions = dashboardActions
    return result
}

// ─── Send Message ──────────────────────────────────────────────────────────

/**
 * Process a user message through the backend pipeline.
 *
 * @param {string} userMessage
 * @param {object} [dashboardState] - current dashboard snapshot (from ChatWidget)
 * @param {object} [options]
 * @param {string} [options.language] - 'de' or 'en'
 * @param {string} [options.currentPage] - portal page name
 * @returns {{ reply: string, actions?: Array, mock?: boolean, debug?: object }}
 */
export async function sendMessage(userMessage, dashboardState, options = {}) {
    if (_mode === 'mock') {
        return _handleMock(userMessage)
    }
    return _handleBackend(userMessage, dashboardState, options)
}

// ─── Reset ─────────────────────────────────────────────────────────────────

export function resetConversation() {
    history.clear()
    diag.log('reset', 'Conversation reset')
    logger.logEvent('conversation_reset')
}

// ─── Internal helpers ──────────────────────────────────────────────────────

/**
 * Split actions into navigation (handled here) and dashboard (passed to ChatWidget).
 * Navigation actions (switch_page, navigate_to_dashboard) are executed immediately.
 */
async function _processActions(actions, language) {
    const dashboardActions = []
    const navigationDescriptions = []

    for (const act of actions) {
        if (act.action === 'switch_page') {
            const page = act.page || 'home'
            const conf = window.conf || {}
            const routeKey = _pageToRouteKey(page)
            const path = conf.ROUTES?.[routeKey]?.path

            if (path && _navigate) {
                _navigate(path)
                diag.log('actions', `Navigation: switch_page → ${page}`)
                navigationDescriptions.push(
                    language === 'de' ? `Navigiert zu ${page}` : `Navigated to ${page}`
                )
            } else {
                diag.error('actions', `switch_page failed: no path for "${page}"`, { routeKey, hasNavigate: !!_navigate })
            }
        } else if (act.action === 'navigate_to_dashboard') {
            const ok = await navigateToDashboard()
            diag.log('actions', `Navigation: navigate_to_dashboard`, { success: ok })
            navigationDescriptions.push(
                language === 'de' ? 'Dashboard geöffnet' : 'Dashboard opened'
            )
        } else if (act.action === 'open_mysite_section') {
            const section = act.section || 'events'
            const panelMap = { report: 'report', diary: 'diary', download: 'download', events: 'archive' }
            const panel = panelMap[section] || section

            if (!_mysiteActions) {
                const conf = window.conf || {}
                const mysitePath = conf.ROUTES?.MYSITE?.path
                if (mysitePath && _navigate) {
                    _navigate(mysitePath)
                    await _poll(() => _mysiteActions, 100, 5000) // eslint-disable-line no-loop-func
                }
            }
            if (_mysiteActions) {
                _mysiteActions.expandSection(panel)
                diag.log('actions', `Navigation: open_mysite_section → ${section} (panel: ${panel})`)
                const sectionNames = language === 'de'
                    ? { report: 'Projektbericht', diary: 'Bautagebuch', download: 'Download', events: 'Ereignisse' }
                    : { report: 'Project Report', diary: 'Construction Diary', download: 'Download', events: 'Events' }
                navigationDescriptions.push(
                    (language === 'de' ? 'Geöffnet: ' : 'Opened: ') + (sectionNames[section] || section)
                )
            } else {
                diag.error('actions', `open_mysite_section failed: _mysiteActions not available`, { section })
            }
        } else if (act.action === 'set_archive_date') {
            if (!_archiveActions) {
                const conf = window.conf || {}
                const archivePath = conf.ROUTES?.ARCHIVE?.path
                if (archivePath && _navigate) {
                    _navigate(archivePath)
                    await _poll(() => _archiveActions, 100, 5000) // eslint-disable-line no-loop-func
                }
            }
            if (_archiveActions) {
                const dateStr = act.date || ''
                const timeStr = act.time || ''
                const dateTimeStr = timeStr ? `${dateStr} ${timeStr}` : `${dateStr} 07:00`
                _archiveActions.setDate(dateTimeStr)
                diag.log('actions', `Navigation: set_archive_date → ${dateTimeStr}`)
                const desc = timeStr
                    ? `Archiv: ${dateStr} ${timeStr}`
                    : `Archiv: ${dateStr}`
                navigationDescriptions.push(
                    language === 'de' ? desc : desc.replace('Archiv', 'Archive')
                )
            } else {
                diag.error('actions', 'set_archive_date failed: _archiveActions not available')
            }
        } else if (act.action === 'switch_tab') {
            const tabMap = { erkennungen: 'detections', wetter: 'weather' }
            const tab = tabMap[act.tab] || act.tab
            if (!_dashboardActions) {
                await navigateToDashboard()
                await _poll(() => _dashboardActions, 100, 5000) // eslint-disable-line no-loop-func
            }
            if (_dashboardActions) {
                _dashboardActions.switchTab(tab)
                diag.log('actions', `Dashboard: switch_tab → ${tab}`)
                navigationDescriptions.push(
                    language === 'de' ? `Tab: ${tab}` : `Tab: ${tab}`
                )
            } else {
                diag.error('actions', `switch_tab failed: _dashboardActions not available`, { tab })
            }
        } else {
            dashboardActions.push(act)
            diag.log('actions', `Dashboard action passed through: ${act.action}`, act)
        }
    }

    return { dashboardActions, navigationDescriptions }
}

function _pageToRouteKey(page) {
    const map = {
        home: 'HOME', live: 'LIVE', timelapse: 'TIMELAPSE',
        archive: 'ARCHIVE', mysite: 'MYSITE', access: 'ACCESS',
    }
    return map[page] || 'HOME'
}

/** Derive current page name from window.location.pathname. */
function _getCurrentPage() {
    const path = window.location.pathname
    const conf = window.conf || {}
    const routes = conf.ROUTES || {}
    for (const [key, route] of Object.entries(routes)) {
        if (route.path === path) return key.toLowerCase()
    }
    return 'home'
}

function _poll(predicate, intervalMs, timeoutMs) {
    return new Promise(resolve => {
        let elapsed = 0
        const id = setInterval(() => {
            if (predicate()) { clearInterval(id); resolve(true) }
            elapsed += intervalMs
            if (elapsed >= timeoutMs) { clearInterval(id); resolve(false) }
        }, intervalMs)
    })
}

// ─── Mock Mode ─────────────────────────────────────────────────────────────

const MOCK_RESPONSES = [
    'Das Projekt begann am 5. November 2025. Der Rohbau ist seit Ende Januar 2026 weitgehend fertiggestellt.',
    'Aktuell sind auf der Baustelle diverse Geräte im Einsatz, darunter Bagger, Radlader und LKW.',
    'Ich bin der Baustellenassistent. Ich kann dir Informationen zum Baufortschritt, Erkennungsdaten und Wetter geben.',
    'Du kannst mich nach Fahrzeugerkennungen, Wetterdaten oder Projektphasen fragen.',
]
let _mockCounter = 0

function _handleMock(userMessage) {
    history.addMessage('user', userMessage)
    const reply = MOCK_RESPONSES[_mockCounter % MOCK_RESPONSES.length]
    _mockCounter++
    history.addMessage('assistant', reply, { model: 'mock' })
    diag.log('mock', 'Mock response', { reply_len: reply.length })
    return { reply, mock: true }
}
